import type { ReactNode } from 'react'

import { Brand } from './Brand'
import { NotchedPanel } from './NotchedPanel'
import { ScreenBackground } from './ScreenBackground'

/**
 * The onboarding/auth shell used by 9 screens (design-spec.md §1 and §5.9).
 *
 * A 252px navy hero carrying the two glows and the brand block, then a panel
 * with a 20px top radius and a notch cut out of its top centre, holding a
 * 72×8 pill. It is *not* a bottom sheet: there is no scrim and it never moves.
 *
 * Content is 28px in from each edge with a 37px vertical rhythm. §1 says it
 * starts 52px inside the panel, which is what screens 3, 7 and 9 do — but the
 * OTP screens place it at 114. The design is hand-made rather than
 * componentised (§8.2), so the offset is a prop instead of a constant.
 */
interface AuthPanelProps {
  children: ReactNode
  /** Distance from the panel's top edge to the first line of content. */
  contentTop?: number
  /** Vertical rhythm between blocks. 37 on most frames, 20 on the two
   *  login-side ones (`202:250`, `202:283`). */
  gap?: number
}

export function AuthPanel({ children, contentTop = 52, gap = 37 }: AuthPanelProps) {
  return (
    <ScreenBackground variant="auth" className="flex flex-col">
      <div className="relative z-10 flex h-[var(--sx-auth-hero-h)] shrink-0 justify-center pt-[67px]">
        <Brand />
      </div>

      <NotchedPanel className="z-10 flex min-h-0 flex-1 flex-col">
        <div
          className="sx-col-auth relative flex min-h-0 flex-1 flex-col pb-8"
          style={{ paddingTop: contentTop, gap }}
        >
          {children}
        </div>
      </NotchedPanel>
    </ScreenBackground>
  )
}
